/**
 * Add Friend Dialog Component
 * Coláiste Lurgan Management System - Student friendship management
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useStudents, useAddFriendship, useStudentFriendships } from '../../hooks/useStudents';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { Search, UserPlus, X, AlertCircle, Loader2, Home, CheckCircle2 } from 'lucide-react';

export function AddFriendDialog({ open, onOpenChange, student, currentFriends = [] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [step, setStep] = useState('search'); // 'search' | 'house_prompt'
  const [addedFriend, setAddedFriend] = useState(null);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch potential friends with server-side search
  // Only fetch when user has typed at least 2 characters (avoid loading 100 students on open)
  const { data: studentsData, isLoading } = useStudents({
    course: student?.course,
    gender: student?.gender,
    status: 'ACTIVE',
    search: debouncedSearchTerm, // Server-side search (backend searches first_name, last_name, student_id)
    page_size: 100 // Use maximum page size to get more results
  }, {
    enabled: open && !!student && debouncedSearchTerm.length >= 2 // Only search with 2+ characters
  });

  const addFriendshipMutation = useAddFriendship({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['friendships']);

      // If student has a house and the new friend isn't already in it,
      // show the housing prompt instead of closing immediately
      if (student.house && selectedStudent?.house !== student.house) {
        setAddedFriend(selectedStudent);
        setStep('house_prompt');
      } else {
        toast({
          title: t('friendships.addSuccess'),
          description: t('friendships.addSuccessDescription'),
          variant: 'success',
        });
        setSelectedStudent(null);
        setSearchTerm('');
        setDebouncedSearchTerm('');
        onOpenChange(false);
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.response?.data?.non_field_errors?.[0] ||
                          t('friendships.addError');
      toast({
        title: t('friendships.addErrorTitle'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const houseMoveMutation = useMutation({
    mutationFn: ({ friendUuid, houseUuid }) => api.allocation.move([friendUuid], houseUuid),
    onSuccess: () => {
      queryClient.invalidateQueries(['allocation', student.course]);
      queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(student.uuid) });
      if (addedFriend) {
        queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(addedFriend.uuid) });
      }
      toast({
        title: t('friendships.movedToHouse', { house: student.house_name }),
        variant: 'success',
      });
      handleClose();
    },
    // onError: stay on prompt and show the full error inline
  });

  // Calculate friend group size for current student
  const currentGroupSize = useMemo(() => {
    if (!currentFriends || currentFriends.length === 0) return 1; // Just the student themselves

    // Build a set of all students in the friendship network (transitive)
    const friendNetwork = new Set([student.uuid]);
    const queue = [...currentFriends.map(f =>
      f.student1 === student.uuid ? f.student2 : f.student1
    )];

    while (queue.length > 0) {
      const friendUuid = queue.shift();
      if (!friendNetwork.has(friendUuid)) {
        friendNetwork.add(friendUuid);
        // Find friends of this friend
        const friendsOfFriend = currentFriends
          .filter(f => f.student1 === friendUuid || f.student2 === friendUuid)
          .map(f => f.student1 === friendUuid ? f.student2 : f.student1)
          .filter(uuid => !friendNetwork.has(uuid));
        queue.push(...friendsOfFriend);
      }
    }

    return friendNetwork.size;
  }, [currentFriends, student]);

  // Get current friend UUIDs for filtering
  const currentFriendUuids = useMemo(() => {
    const uuids = new Set([student.uuid]); // Exclude self
    currentFriends?.forEach(f => {
      uuids.add(f.student1);
      uuids.add(f.student2);
    });
    return uuids;
  }, [currentFriends, student]);

  // Filter and categorize potential friends
  // Note: Backend handles search filtering, we only need to exclude current friends and check group size
  const potentialFriends = useMemo(() => {
    if (!studentsData?.results) return [];

    const students = studentsData.results;

    return students
      .filter(s => !currentFriendUuids.has(s.uuid)) // Not already friends
      .map(s => {
        // Determine why student can't be added (if applicable)
        let disabled = false;
        let reason = null;

        if (currentGroupSize >= 3) {
          disabled = true;
          reason = t('friendships.groupFull');
        }

        return {
          ...s,
          disabled,
          reason,
        };
      })
      .sort((a, b) => {
        // Sort: enabled first, then by name
        if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
  }, [studentsData, currentFriendUuids, currentGroupSize, t]);

  const handleAddFriend = () => {
    if (!selectedStudent) return;

    addFriendshipMutation.mutate({
      student1: student.uuid,
      student2: selectedStudent.uuid,
      // created_by is set automatically by backend from request.user
    });
  };

  const handleClose = () => {
    setSelectedStudent(null);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStep('search');
    setAddedFriend(null);
    onOpenChange(false);
  };

  const isHouseFull = houseMoveMutation.isError &&
    (houseMoveMutation.error?.response?.data?.details?.[0]?.reason === 'capacity');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t('friendships.addFriendTitle')} - {student?.full_name}
          </DialogTitle>
          <DialogDescription>
            {step === 'house_prompt'
              ? t('friendships.housePromptSubtitle')
              : `${t('friendships.addFriendDescription')} (${currentGroupSize} of 3)`}
          </DialogDescription>
        </DialogHeader>

        {step === 'search' ? (
          <>
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
              <Input
                placeholder={t('friendships.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Student List */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {debouncedSearchTerm.length < 2 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                  <Search className="h-12 w-12 mb-3 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm font-medium">{t('friendships.typeToSearch')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('friendships.typeToSearchDescription')}</p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
                </div>
              ) : potentialFriends.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                  <AlertCircle className="h-8 w-8 mb-2 text-gray-400 dark:text-gray-500" />
                  <p className="text-sm">{t('friendships.noEligibleStudents')}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {potentialFriends.map((potentialFriend) => (
                    <button
                      key={potentialFriend.uuid}
                      onClick={() => !potentialFriend.disabled && setSelectedStudent(potentialFriend)}
                      disabled={potentialFriend.disabled}
                      className={`w-full text-left p-4 transition-colors ${
                        potentialFriend.disabled
                          ? 'bg-gray-50 dark:bg-gray-800/60 cursor-not-allowed opacity-60'
                          : selectedStudent?.uuid === potentialFriend.uuid
                          ? 'bg-blue-50 dark:bg-blue-950/40 border-l-4 border-blue-500 dark:border-blue-400'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{potentialFriend.full_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {potentialFriend.student_id}
                            </Badge>
                          </div>
                          {potentialFriend.reason && (
                            <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {potentialFriend.reason}
                            </p>
                          )}
                        </div>
                        {selectedStudent?.uuid === potentialFriend.uuid && (
                          <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-500 text-white">
                            <span className="text-sm">✓</span>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* House prompt step */
          <div className="space-y-4 py-2">
            {/* Friendship success confirmation */}
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-md p-3">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>
                {t('friendships.addedToGroup', { name: addedFriend?.full_name })}
              </span>
            </div>

            {/* House prompt */}
            {isHouseFull ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 space-y-1">
                <p className="text-sm font-medium text-destructive">
                  {t('friendships.houseFull', { house: student.house_name })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('friendships.houseFullHint')}
                </p>
              </div>
            ) : (
              <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <Home className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      {t('friendships.housePromptTitle', {
                        name: addedFriend?.full_name,
                        house: student.house_name,
                      })}
                    </p>
                    {addedFriend?.house_name && (
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {t('friendships.currentlyIn', { house: addedFriend.house_name })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'search' ? (
            <>
              <Button variant="outline" onClick={handleClose}>
                <X className="h-4 w-4 mr-2" />
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleAddFriend}
                disabled={!selectedStudent || addFriendshipMutation.isPending}
              >
                {addFriendshipMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('friendships.adding')}
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('friendships.addFriend')}
                  </>
                )}
              </Button>
            </>
          ) : isHouseFull ? (
            <Button onClick={handleClose}>{t('common.close')}</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={houseMoveMutation.isPending}
              >
                {t('friendships.skipHouseAssign')}
              </Button>
              <Button
                onClick={() =>
                  houseMoveMutation.mutate({
                    friendUuid: addedFriend.uuid,
                    houseUuid: student.house,
                  })
                }
                disabled={houseMoveMutation.isPending}
              >
                {houseMoveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.moving')}
                  </>
                ) : (
                  <>
                    <Home className="h-4 w-4 mr-2" />
                    {t('friendships.moveToHouse', { house: student.house_name })}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddFriendDialog;
